// Drawing elements as SVG paths, like the app's Sketch.kt (a rough.js port seeded from the element's
// seed) and Freehand.kt (perfect-freehand with Excalidraw's settings): the same seed gives the same
// wobble on every device. Clean elements (roughness 0) are drawn without wobble, with rounded corners.

import { getStroke } from "perfect-freehand";
import { RoughGenerator } from "roughjs/bin/generator";
import type { Options } from "roughjs/bin/core";
import { type El, bounds, customShape, isStickyNote } from "./model.ts";
import { cylinderLid, outline } from "./shapes.ts";

export interface PathPart {
  d: string;
  stroke: string;
  strokeWidth: number;
  fill: string;
  dash?: string;
}

const generator = new RoughGenerator();

function options(e: El, dark: (c: string) => string): Options {
  const fill = e.backgroundColor && e.backgroundColor !== "transparent" ? dark(e.backgroundColor) : undefined;
  return {
    seed: e.seed ?? 1,
    roughness: e.roughness ?? 1,
    stroke: dark(e.strokeColor ?? "#1e1e1e"),
    strokeWidth: e.strokeWidth ?? 2,
    fill,
    fillStyle: e.fillStyle === "none" ? undefined : (e.fillStyle ?? "hachure"),
    hachureAngle: -41,
    hachureGap: (e.strokeWidth ?? 2) * 4,
    fillWeight: (e.strokeWidth ?? 2) / 2,
    strokeLineDash: e.strokeStyle === "dashed" ? [8, 8 + (e.strokeWidth ?? 2)] : e.strokeStyle === "dotted" ? [1.5, 6 + (e.strokeWidth ?? 2)] : undefined,
    disableMultiStroke: e.strokeStyle !== "solid" && e.strokeStyle !== undefined,
    preserveVertices: (e.roughness ?? 1) < 2,
  };
}

/** The paths of [e] in its own coordinates (x/y included); text and images are drawn elsewhere. */
export function paths(e: El, dark: (c: string) => string = (c) => c): PathPart[] {
  const o = options(e, dark);
  const b = bounds(e);
  const w = b.maxX - b.minX;
  const h = b.maxY - b.minY;
  const rounded = e.roundness && (e.roughness ?? 1) === 0;
  let drawables;
  switch (e.type) {
    case "rectangle": {
      const shape = customShape(e);
      if (shape) {
        const pts = outline(shape, b);
        drawables = [generator.polygon(pts, o)];
        if (shape === "cylinder") drawables.push(generator.linearPath(cylinderLid(b), { ...o, fill: undefined }));
      } else if (rounded || isStickyNote(e)) {
        const r = Math.min(w, h) * 0.25 > 32 ? 32 : Math.min(w, h) * 0.25;
        drawables = [generator.path(`M ${b.minX + r} ${b.minY} L ${b.maxX - r} ${b.minY} Q ${b.maxX} ${b.minY} ${b.maxX} ${b.minY + r} L ${b.maxX} ${b.maxY - r} Q ${b.maxX} ${b.maxY} ${b.maxX - r} ${b.maxY} L ${b.minX + r} ${b.maxY} Q ${b.minX} ${b.maxY} ${b.minX} ${b.maxY - r} L ${b.minX} ${b.minY + r} Q ${b.minX} ${b.minY} ${b.minX + r} ${b.minY}`, o)];
      } else {
        drawables = [generator.rectangle(b.minX, b.minY, w, h, o)];
      }
      break;
    }
    case "ellipse":
      drawables = [generator.ellipse(b.minX + w / 2, b.minY + h / 2, w, h, o)];
      break;
    case "diamond":
      drawables = [generator.polygon([[b.minX + w / 2, b.minY], [b.maxX, b.minY + h / 2], [b.minX + w / 2, b.maxY], [b.minX, b.minY + h / 2]], o)];
      break;
    case "line":
    case "arrow": {
      const pts = (e.points ?? []).map(([x, y]) => [x + e.x, y + e.y] as [number, number]);
      if (pts.length < 2) return [];
      drawables = [pts.length > 2 && !e.elbowed ? generator.curve(pts, { ...o, fill: undefined }) : generator.linearPath(pts, { ...o, fill: undefined })];
      if (e.type === "arrow") {
        if (e.endArrowhead) drawables.push(...arrowhead(pts[pts.length - 2]!, pts[pts.length - 1]!, e, o));
        if (e.startArrowhead) drawables.push(...arrowhead(pts[1]!, pts[0]!, e, o));
      }
      break;
    }
    case "freedraw":
      return [freehand(e, dark)];
    default:
      return [];
  }
  return drawables.flatMap((d) => generator.toPaths(d)).map((p) => ({ d: p.d, stroke: p.stroke, strokeWidth: p.strokeWidth, fill: p.fill ?? "none", dash: o.strokeLineDash?.join(" ") }));
}

function arrowhead(from: [number, number], to: [number, number], e: El, o: Options) {
  const size = Math.min(30, 10 + (e.strokeWidth ?? 2) * 4);
  const angle = Math.atan2(to[1] - from[1], to[0] - from[0]);
  const wing = (a: number): [number, number] => [to[0] - size * Math.cos(angle + a), to[1] - size * Math.sin(angle + a)];
  return [generator.linearPath([wing(0.45), to, wing(-0.45)], { ...o, fill: undefined, strokeLineDash: undefined })];
}

function freehand(e: El, dark: (c: string) => string): PathPart {
  const pts = (e.points ?? []).map(([x, y], i) => [x + e.x, y + e.y, e.pressures?.[i] ?? 0.5]);
  const outline = getStroke(pts, { size: (e.strokeWidth ?? 2) * 4.25, thinning: 0.6, smoothing: 0.5, streamline: 0.5, simulatePressure: e.simulatePressure !== false, last: true });
  const d = outline.length ? `M ${outline.map(([x, y]) => `${x!.toFixed(2)} ${y!.toFixed(2)}`).join(" L ")} Z` : "";
  const color = dark(e.strokeColor ?? "#1e1e1e");
  return { d, stroke: "none", strokeWidth: 0, fill: color };
}

/** The colours on a dark canvas (BoardColors.toDark): inverted lightness, hue kept; stored colours stay light. */
export function toDark(color: string): string {
  if (!/^#[0-9a-f]{6}$/i.test(color)) return color;
  const n = parseInt(color.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => v / 255) as [number, number, number];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return hsl(0, 0, 1 - l);
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const hue = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return hsl(hue * 60, s, Math.min(0.92, Math.max(0.18, 1 - l)));
}

function hsl(h: number, s: number, l: number): string {
  return `hsl(${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%)`;
}
